import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Progress, Space, Table, Tooltip } from 'tdesign-react';
import { useMemoizedFn } from 'ahooks';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { CheckCircleFilledIcon, ErrorTriangleIcon, LoadingIcon } from 'tdesign-icons-react';
import useTable from '@/hooks/useTable';
import { TOPIC_HANDLE_STATUS, handleErrorTopic } from '@/slices/clusterSlice';
import { renderYesNo } from './TopicCheck';

function TopicCheck(
  { recoveryClusterName, primaryClusterName, bindRecovery, handleErrorTopic },
  ref,
) {
  const { errorTopics, errorTopicsNum, handleDone } = bindRecovery;
  const tableRef = useRef(null);
  const [percent, setPercent] = useState(0);

  const columns = [
    {
      title: 'Topic',
      colKey: 'topicName',
      cell: ({ row }) => {
        return (
          <TopicCell>
            {row.missPartitions && row.handled !== TOPIC_HANDLE_STATUS.HANDLED && (
              <Tooltip content='当前topic在主集群存在partition缺失的情况，将会在下一步处理中尝试修复'>
                <ErrorTriangleIcon color='var(--td-error-color)' />
              </Tooltip>
            )}
            <div className='cell-body'>{row.topicName}</div>
          </TopicCell>
        );
      },
    },
    {
      title: '是否存在',
      colKey: 'existed',
      width: 100,
      cell: renderYesNo,
    },
    {
      title: '分区一致',
      colKey: 'consistentPartitions',
      width: 100,
      cell: (arg1) => renderYesNo({ ...arg1, primaryClusterName, recoveryClusterName }),
    },
    {
      title: '副本一致',
      colKey: 'consistentReplicas',
      width: 100,
      cell: (arg1) => renderYesNo({ ...arg1, primaryClusterName, recoveryClusterName }),
    },
    {
      title: '权限一致',
      colKey: 'consistentPermissions',
      width: 100,
      cell: (arg1) => renderYesNo({ ...arg1, primaryClusterName, recoveryClusterName }),
    },
    {
      title: '处理结果',
      colKey: 'handled',
      width: 100,
      cell: ({ row }) => (
        <>
          {/* {row.handled === TOPIC_HANDLE_STATUS.UNHANDLED && '等待处理'} */}
          {row.handled === TOPIC_HANDLE_STATUS.HANDLING && <LoadingIcon className='t-loading' />}
          {row.handled === TOPIC_HANDLE_STATUS.HANDLED && <CheckCircleFilledIcon color='green' />}
          {row.handled === TOPIC_HANDLE_STATUS.ERROR && (
            <Space breakLine size={4}>
              <ErrorTriangleIcon color='var(--td-error-color)' />
              {row.errorMsg}
            </Space>
          )}
        </>
      ),
    },
  ];

  const { config } = useTable({
    columns,
    largeData: true,
  });

  const scrollToElement = (index) => {
    tableRef.current.scrollToElement({
      index,
      top: 48,
      behavior: 'smooth',
    });
  };

  const getNextErrorTopic = () => {
    const currentTopicIndex = errorTopics.findIndex(
      (item) =>
        item.handled === TOPIC_HANDLE_STATUS.UNHANDLED ||
        item.handled === TOPIC_HANDLE_STATUS.ERROR,
    );
    if (currentTopicIndex > -1) {
      // 滚动到对应位置
      scrollToElement(currentTopicIndex);
      // 计算百分比
      const realPercent = (currentTopicIndex / errorTopics.length || 0) * 100;
      setPercent(Math.round(realPercent));

      return errorTopics[currentTopicIndex];
    }
    return false;
  };

  // 开始检查
  const startHandle = useMemoizedFn(async () => {
    // 开始检查
    const nextTopic = getNextErrorTopic();
    if (!nextTopic) {
      return;
    }
    // 模拟处理
    const { type } = await handleErrorTopic({
      primaryClusterName,
      recoveryClusterName,
      results: [nextTopic],
    });
    // 没有失败，继续处理
    if (type !== 'cluster/handleErrorTopic/rejected') {
      startHandle();
    }
  });

  useImperativeHandle(ref, () => ({
    doRetry: startHandle,
  }));

  useEffect(() => {
    startHandle();
  }, [startHandle]);

  return (
    <>
      <Header>
        处理进度：
        <Progress
          theme='plump'
          percentage={handleDone ? 100 : percent}
          strokeWidth={12}
          style={{ flex: 1, margin: '0 20px 0 10px' }}
        />
        {errorTopics?.filter((item) => item.handled === TOPIC_HANDLE_STATUS.HANDLED).length}/
        {errorTopicsNum}
      </Header>
      <Table
        className='topic-check-table'
        {...config}
        ref={tableRef}
        rowKey='topicName'
        rowClassName='fadeIn'
        data={errorTopics}
        scroll={{ type: 'scroll', threshold: 1 }}
      />
    </>
  );
}

const TopicCheckRef = React.forwardRef(TopicCheck);

export default connect(
  (state) => ({ bindRecovery: state.cluster.bindRecovery }),
  {
    handleErrorTopic,
  },
  null,
  { forwardRef: true },
)(TopicCheckRef);

const Header = styled.div`
  display: flex;
  align-items: center;
  padding: 0 20px 0 15px;
  margin-bottom: 28px;
`;

const TopicCell = styled.div`
  display: flex;
  gap: 4px;
  .t-icon {
    flex-shrink: 0;
    height: 25px;
  }
  .cell-body {
    flex: 1;
    width: 0;
  }
`;
